import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { HexColorPicker } from 'react-colorful';
import { Upload, Download, Palette, RotateCcw, LayoutTemplate, Pipette, Check, ImagePlus } from 'lucide-react';

const COLOR_PRESETS = ['#FFFFFF', '#000000', '#0F0F0F', '#FF6B35', '#F0A830', '#FFCB05', '#E60012', '#4ECDC4', '#0095DA', '#7B2D8B', '#8B5CF6', '#059669'];

const isValidHex = (v) => /^#([0-9a-fA-F]{6})$/.test(v);

const ColorPickerPopover = ({ label, value, onChange, testid }) => {
  const [open, setOpen] = React.useState(false);
  const [hexInput, setHexInput] = React.useState(value);
  const supportsEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;

  React.useEffect(() => { setHexInput(value); }, [value]);

  const handleHexInput = (v) => {
    if (!v.startsWith('#')) v = '#' + v.replace(/#/g, '');
    v = v.slice(0, 7);
    setHexInput(v);
    if (isValidHex(v)) onChange(v.toUpperCase());
  };

  const pickWithEyeDropper = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      // eslint-disable-next-line no-undef
      const ed = new window.EyeDropper();
      const res = await ed.open();
      if (res?.sRGBHex) onChange(res.sRGBHex.toUpperCase());
      // keep the popover open after picking
    } catch (_) { /* user cancelled */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex items-center gap-1.5 text-xs text-[#999] cursor-pointer" data-testid={`${testid}-trigger`}>
          <span>{label}</span>
          <span className="w-7 h-7 rounded border border-[#333] shadow-inner" style={{ backgroundColor: value }} data-testid={testid} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 p-3 bg-[#1a1a1a] border-[#333] space-y-3"
        data-testid={`${testid}-popover`}
        onInteractOutside={() => setOpen(false)}
      >
        <div className="banner-color-picker">
          <HexColorPicker color={isValidHex(value) ? value : '#000000'} onChange={(c) => onChange(c.toUpperCase())} />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded border border-[#333] shrink-0" style={{ backgroundColor: value }} />
          <input
            value={hexInput}
            onChange={(e) => handleHexInput(e.target.value)}
            className="flex-1 min-w-0 bg-[#0f0f0f] border border-[#333] rounded px-2 py-1 text-xs text-white uppercase"
            data-testid={`${testid}-hex`}
          />
          {supportsEyeDropper && (
            <button type="button" onClick={pickWithEyeDropper} title="Pipette"
              className="p-1.5 rounded border border-[#333] text-[#999] hover:text-[#FF6B35] hover:border-[#FF6B35] transition-colors shrink-0"
              data-testid={`${testid}-eyedropper`}>
              <Pipette className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="grid grid-cols-6 gap-1.5">
          {COLOR_PRESETS.map((c) => (
            <button key={c} type="button" onClick={() => onChange(c)}
              className={`w-6 h-6 rounded border ${value?.toUpperCase() === c ? 'border-white ring-1 ring-white' : 'border-[#333]'}`}
              style={{ backgroundColor: c }} data-testid={`${testid}-preset-${c.replace('#', '')}`} />
          ))}
        </div>
        <button type="button" onClick={() => setOpen(false)}
          className="w-full flex items-center justify-center gap-1.5 bg-[#FF6B35] hover:bg-[#E55A25] text-white text-xs font-medium rounded py-1.5 transition-colors"
          data-testid={`${testid}-ok`}>
          <Check className="w-3.5 h-3.5" /> OK
        </button>
      </PopoverContent>
    </Popover>
  );
};

const CANVAS_W = 900;
const CANVAS_H = 300;
const MAX_SIZE_KB = 150;

const GAME_LOGOS = {
  magic: { color: '#F0A830' }, pokemon: { color: '#FFCB05' }, yugioh: { color: '#7B2D8B' },
  lorcana: { color: '#0095DA' }, onepiece: { color: '#E60012' }, dbs: { color: '#F47B20' },
  altered: { color: '#8B5CF6' }, fab: { color: '#DC2626' }, riftbound: { color: '#059669' },
  starwars: { color: '#FFE81F' }, sorcery: { color: '#6B21A8' },
};

const LAYOUTS = [
  { value: 'image-right', label: 'Image à droite' },
  { value: 'image-left', label: 'Image à gauche' },
  { value: 'image-bg', label: 'Image en fond' },
  { value: 'image-bg-center', label: 'Image en fond (centré)' },
  { value: 'bottom-bar', label: 'Barre inférieure' },
  { value: 'side-accent', label: 'Bande accent + vignette' },
  { value: 'minimal', label: 'Minimaliste (sans image)' },
  { value: 'diagonal', label: 'Diagonale' },
];

const presetBgColors = ['#1a1a2e', '#0f3460', '#16213e', '#2d132c', '#0a1628', '#1a0a2e', '#0d1117', '#1c1917'];

// --- Canvas helpers ---
function wrapText(ctx, text, maxWidth, maxLines = 3) {
  const words = (text || '').split(' ');
  const lines = [];
  let current = '';
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current); current = word;
    } else { current = test; }
  }
  if (current) lines.push(current);
  return lines.slice(0, maxLines);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export default function BannerEditor({ shopName, shopLogoUrl, tournamentName, gameName, gameId, dateStr, format, address, defaultBgColor, onCoverImage, onBannerReady }) {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const imgRegionRef = useRef(null);

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scale = CANVAS_W / rect.width;
    const cx = (e.clientX - rect.left) * scale;
    const cy = (e.clientY - rect.top) * scale;
    const r = imgRegionRef.current;
    // Click inside the image area (or anywhere if this layout has no image slot) opens the file picker
    if (!r || (cx >= r.x && cx <= r.x + r.w && cy >= r.y && cy <= r.y + r.h)) {
      fileInputRef.current?.click();
    }
  };

  const [layout, setLayout] = useState('image-right');
  const [bgColor, setBgColor] = useState(defaultBgColor || '#1a1a2e');
  const [textColor, setTextColor] = useState('#ffffff');
  const [highlightColor, setHighlightColor] = useState(GAME_LOGOS[gameId]?.color || '#FF6B35');
  const [showFormat, setShowFormat] = useState(true);
  const [showAddress, setShowAddress] = useState(false);
  const [showLogo, setShowLogo] = useState(true);
  const [customPhrase, setCustomPhrase] = useState('');

  const [uploadedImgEl, setUploadedImgEl] = useState(null);
  const [shopLogoEl, setShopLogoEl] = useState(null);

  // Default highlight follows the game color
  useEffect(() => {
    setHighlightColor(GAME_LOGOS[gameId]?.color || '#FF6B35');
  }, [gameId]);

  useEffect(() => {
    if (shopLogoUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => setShopLogoEl(img);
      img.onerror = () => setShopLogoEl(null);
      img.src = shopLogoUrl;
    } else { setShopLogoEl(null); }
  }, [shopLogoUrl]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => setUploadedImgEl(img);
      img.src = ev.target.result;
      if (onCoverImage) onCoverImage(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = CANVAS_W, h = CANVAS_H;
    const hl = highlightColor || '#FF6B35';

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, w, h);
    imgRegionRef.current = null;

    // Draw an uploaded image (cover-fit) inside a rect; optional fade on one side
    const drawImg = (x, y, rw, rh, fade) => {
      imgRegionRef.current = { x, y, w: rw, h: rh };
      if (!uploadedImgEl) {
        ctx.fillStyle = '#ffffff10';
        ctx.fillRect(x + 10, y + 10, rw - 20, rh - 20);
        ctx.strokeStyle = '#ffffff30';
        ctx.setLineDash([8, 6]);
        ctx.strokeRect(x + 10, y + 10, rw - 20, rh - 20);
        ctx.setLineDash([]);
        ctx.fillStyle = '#ffffff40';
        ctx.font = '14px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('+ Cliquez pour ajouter une image', x + rw / 2, y + rh / 2);
        ctx.textAlign = 'left';
        return;
      }
      const imgRatio = uploadedImgEl.width / uploadedImgEl.height;
      const secRatio = rw / rh;
      let sx = 0, sy = 0, sw = uploadedImgEl.width, sh = uploadedImgEl.height;
      if (imgRatio > secRatio) { sw = uploadedImgEl.height * secRatio; sx = (uploadedImgEl.width - sw) / 2; }
      else { sh = uploadedImgEl.width / secRatio; sy = (uploadedImgEl.height - sh) / 2; }
      ctx.drawImage(uploadedImgEl, sx, sy, sw, sh, x, y, rw, rh);
      if (fade === 'left') {
        const g = ctx.createLinearGradient(x, 0, x + 70, 0);
        g.addColorStop(0, bgColor); g.addColorStop(1, bgColor + '00');
        ctx.fillStyle = g; ctx.fillRect(x, y, 70, rh);
      } else if (fade === 'right') {
        const g = ctx.createLinearGradient(x + rw - 70, 0, x + rw, 0);
        g.addColorStop(0, bgColor + '00'); g.addColorStop(1, bgColor);
        ctx.fillStyle = g; ctx.fillRect(x + rw - 70, y, 70, rh);
      }
    };

    // Paint the info block. align: 'left' | 'center'. Returns next y.
    const paintInfo = (ox, oy, maxW, align = 'left') => {
      const ax = ox;
      let y = oy;
      // Shop logo + name
      if (showLogo && shopLogoEl) {
        const lx = align === 'center' ? ax - 22 : ax;
        ctx.save();
        ctx.beginPath();
        ctx.arc(lx + 22, y + 20, 20, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(shopLogoEl, lx, y, 40, 40);
        ctx.restore();
        if (shopName && align === 'left') {
          ctx.fillStyle = textColor;
          ctx.font = 'bold 24px Arial, sans-serif';
          ctx.textAlign = 'left';
          ctx.fillText(shopName, lx + 56, y + 28);
        }
        y += 58;
      } else if (shopName) {
        ctx.fillStyle = textColor + 'cc';
        ctx.font = 'bold 24px Arial, sans-serif';
        ctx.textAlign = align;
        ctx.fillText(shopName, ax, y + 18);
        y += 46;
      }

      // Tournament name
      ctx.fillStyle = textColor;
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.textAlign = align;
      const lines = wrapText(ctx, tournamentName || 'Nom du tournoi', maxW, 2);
      lines.forEach((line, i) => ctx.fillText(line, ax, y + i * 34));
      y += lines.length * 34 + 10;

      // Badges (game + format)
      const parts = [gameName, (showFormat && format) ? format : null].filter(Boolean);
      if (parts.length) {
        if (align === 'center') {
          ctx.font = 'bold 14px Arial, sans-serif';
          ctx.fillStyle = hl;
          ctx.textAlign = 'center';
          ctx.fillText(parts.join('   •   '), ax, y + 4);
          y += 30;
        } else {
          let bx = ax;
          ctx.font = 'bold 13px Arial, sans-serif';
          ctx.textAlign = 'left';
          if (gameName) {
            const bw = ctx.measureText(gameName).width + 24;
            ctx.fillStyle = hl + '33'; roundRect(ctx, bx, y - 14, bw, 26, 13); ctx.fill();
            ctx.fillStyle = hl; ctx.fillText(gameName, bx + 12, y + 4);
            bx += bw + 8;
          }
          if (showFormat && format) {
            const bw = ctx.measureText(format).width + 24;
            ctx.fillStyle = textColor + '22'; roundRect(ctx, bx, y - 14, bw, 26, 13); ctx.fill();
            ctx.fillStyle = textColor; ctx.fillText(format, bx + 12, y + 4);
          }
          y += 34;
        }
      }

      // Date
      if (dateStr) {
        ctx.fillStyle = textColor + 'cc';
        ctx.font = '15px Arial, sans-serif';
        ctx.textAlign = align;
        ctx.fillText(dateStr, ax, y + 4);
        y += 24;
      }
      // Address
      if (showAddress && address) {
        ctx.fillStyle = textColor + 'aa';
        ctx.font = '13px Arial, sans-serif';
        ctx.textAlign = align;
        const addr = address.length > 60 ? address.slice(0, 57) + '…' : address;
        ctx.fillText(addr, ax, y + 2);
        y += 22;
      }
      // Custom phrase
      if (customPhrase) {
        ctx.fillStyle = hl;
        ctx.font = 'italic bold 16px Arial, sans-serif';
        ctx.textAlign = align;
        ctx.fillText(customPhrase, ax, y + 4);
        y += 24;
      }
      ctx.textAlign = 'left';
      return y;
    };

    switch (layout) {
      case 'image-left': {
        const imgW = w * 0.42;
        drawImg(0, 0, imgW, h, 'right');
        ctx.strokeStyle = hl + '80'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(imgW + 8, 18); ctx.lineTo(imgW + 8, h - 18); ctx.stroke();
        paintInfo(imgW + 32, 38, w - imgW - 64, 'left');
        break;
      }
      case 'image-bg': {
        drawImg(0, 0, w, h);
        const g = ctx.createLinearGradient(0, 0, w, 0);
        g.addColorStop(0, 'rgba(0,0,0,0.82)'); g.addColorStop(0.6, 'rgba(0,0,0,0.45)'); g.addColorStop(1, 'rgba(0,0,0,0.1)');
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
        paintInfo(36, 44, w * 0.62, 'left');
        break;
      }
      case 'image-bg-center': {
        drawImg(0, 0, w, h);
        ctx.fillStyle = 'rgba(0,0,0,0.58)'; ctx.fillRect(0, 0, w, h);
        paintInfo(w / 2, 46, w * 0.82, 'center');
        break;
      }
      case 'bottom-bar': {
        const barH = 92;
        drawImg(0, 0, w, h - barH);
        ctx.fillStyle = bgColor; ctx.fillRect(0, h - barH, w, barH);
        ctx.fillStyle = hl; ctx.fillRect(0, h - barH, w, 4);
        // Title
        ctx.fillStyle = textColor; ctx.font = 'bold 24px Arial, sans-serif'; ctx.textAlign = 'left';
        ctx.fillText(wrapText(ctx, tournamentName || 'Nom du tournoi', w - 280, 1)[0] || '', 30, h - barH + 38);
        // game/format + date
        const parts = [gameName, (showFormat && format) ? format : null, dateStr].filter(Boolean);
        ctx.fillStyle = textColor + 'cc'; ctx.font = '14px Arial, sans-serif';
        ctx.fillText(parts.join('  •  '), 30, h - barH + 66);
        if (showLogo && shopLogoEl) {
          ctx.save(); ctx.beginPath(); ctx.arc(w - 56, h - barH / 2, 28, 0, Math.PI * 2); ctx.clip();
          ctx.drawImage(shopLogoEl, w - 84, h - barH / 2 - 28, 56, 56); ctx.restore();
        }
        if (customPhrase) { ctx.fillStyle = hl; ctx.font = 'italic bold 14px Arial, sans-serif'; ctx.textAlign = 'right'; ctx.fillText(customPhrase, w - (shopLogoEl && showLogo ? 100 : 30), h - barH + 38); ctx.textAlign = 'left'; }
        break;
      }
      case 'side-accent': {
        ctx.fillStyle = hl; ctx.fillRect(0, 0, 14, h);
        const thumbW = 200;
        ctx.save();
        roundRect(ctx, w - thumbW - 24, 28, thumbW, h - 56, 16);
        ctx.clip();
        drawImg(w - thumbW - 24, 28, thumbW, h - 56);
        ctx.restore();
        paintInfo(44, 42, w - thumbW - 90, 'left');
        break;
      }
      case 'minimal': {
        // Decorative highlight underline drawn after title via paintInfo; keep clean bg
        const endY = paintInfo(w / 2, 54, w * 0.84, 'center');
        ctx.strokeStyle = hl; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(w / 2 - 50, endY + 6); ctx.lineTo(w / 2 + 50, endY + 6); ctx.stroke();
        break;
      }
      case 'diagonal': {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(w * 0.52, 0); ctx.lineTo(w, 0); ctx.lineTo(w, h); ctx.lineTo(w * 0.40, h); ctx.closePath();
        ctx.clip();
        drawImg(w * 0.40, 0, w * 0.60, h);
        ctx.restore();
        ctx.strokeStyle = hl; ctx.lineWidth = 4;
        ctx.beginPath(); ctx.moveTo(w * 0.52, 0); ctx.lineTo(w * 0.40, h); ctx.stroke();
        paintInfo(32, 40, w * 0.46, 'left');
        break;
      }
      case 'image-right':
      default: {
        const leftW = w * 0.55;
        drawImg(leftW, 0, w - leftW, h, 'left');
        ctx.strokeStyle = hl + '80'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(leftW - 8, 18); ctx.lineTo(leftW - 8, h - 18); ctx.stroke();
        paintInfo(30, 38, leftW - 60, 'left');
        break;
      }
    }

    // Branding watermark removed per request.
  }, [layout, bgColor, textColor, highlightColor, showFormat, showAddress, showLogo, customPhrase, shopName, shopLogoEl, tournamentName, gameName, dateStr, format, address, uploadedImgEl]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  const exportBanner = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    let quality = 0.92, blob;
    while (quality > 0.1) {
      blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (blob && blob.size <= MAX_SIZE_KB * 1024) break;
      quality -= 0.08;
    }
    if (blob && onBannerReady) onBannerReady(blob);
    return blob;
  }, [onBannerReady]);

  useEffect(() => {
    const timer = setTimeout(() => { exportBanner(); }, 300);
    return () => clearTimeout(timer);
  }, [exportBanner, drawCanvas]);

  const downloadBanner = async () => {
    const blob = await exportBanner();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `banniere_${(tournamentName || 'event').replace(/\s/g, '_')}.jpg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const Toggle = ({ active, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-2.5 py-1 rounded-full text-xs border transition-all ${active ? 'bg-[#FF6B35]/20 border-[#FF6B35] text-[#FF6B35]' : 'border-[#333] text-[#666] hover:text-white'}`}>
      {children}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="banner-editor">
      {/* Layout selector */}
      <div className="flex items-center gap-2">
        <LayoutTemplate className="w-4 h-4 text-[#999]" />
        <span className="text-sm text-[#999]">Disposition</span>
        <Select value={layout} onValueChange={setLayout}>
          <SelectTrigger className="w-auto min-w-[200px] h-9 bg-[#252525] border-[#333] text-white text-xs" data-testid="banner-layout-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-[#333] text-white">
            {LAYOUTS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Canvas Preview */}
      <div className="rounded-lg overflow-hidden border border-[#333] bg-[#0a0a0a]">
        <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} onClick={handleCanvasClick}
          className="w-full h-auto cursor-pointer" title="Cliquez sur l'emplacement image pour importer une photo" data-testid="banner-canvas" />
      </div>

      {/* Custom phrase */}
      <Input value={customPhrase} onChange={(e) => setCustomPhrase(e.target.value.slice(0, 60))}
        placeholder="Phrase personnalisée (ex: Inscriptions ouvertes !)" maxLength={60}
        className="bg-[#252525] border-[#333] text-white text-sm" data-testid="banner-phrase-input" />

      {/* Toggles */}
      <div className="flex flex-wrap items-center gap-2">
        <Toggle active={showFormat} onClick={() => setShowFormat((v) => !v)} testid="banner-toggle-format">Format</Toggle>
        <Toggle active={showAddress} onClick={() => setShowAddress((v) => !v)} testid="banner-toggle-address">Adresse</Toggle>
        <Toggle active={showLogo} onClick={() => setShowLogo((v) => !v)} testid="banner-toggle-logo">Logo</Toggle>
      </div>

      {/* Colors */}
      <div className="flex flex-wrap items-center gap-4">
        <Palette className="w-4 h-4 text-[#666]" />
        <ColorPickerPopover label="Texte" value={textColor} onChange={setTextColor} testid="banner-color-text" />
        <ColorPickerPopover label="Fond" value={bgColor} onChange={setBgColor} testid="banner-color-bg" />
        <ColorPickerPopover label="Surlignage" value={highlightColor} onChange={setHighlightColor} testid="banner-color-highlight" />
        <div className="flex items-center gap-1.5">
          {presetBgColors.map((c) => (
            <button key={c} type="button" onClick={() => setBgColor(c)} title={c}
              className={`w-5 h-5 rounded-full border-2 transition-all ${bgColor === c ? 'border-white scale-125' : 'border-[#333] hover:border-[#666]'}`}
              style={{ backgroundColor: c }} />
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="sr-only" data-testid="banner-file-input" />
        <Button type="button" size="sm" onClick={() => fileInputRef.current?.click()} className="gap-1.5 bg-[#FF6B35] hover:bg-[#E55A25] text-white" data-testid="banner-add-image-btn">
          <ImagePlus className="w-3.5 h-3.5" />Ajouter une image
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setUploadedImgEl(null)} className="gap-1.5 border-[#333] text-white hover:bg-[#252525]">
          <RotateCcw className="w-3.5 h-3.5" />Retirer l'image
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={downloadBanner} className="gap-1.5 border-[#333] text-white hover:bg-[#252525]" data-testid="banner-download-btn">
          <Download className="w-3.5 h-3.5" />Télécharger
        </Button>
      </div>
    </div>
  );
}
